import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { initVisitorData, TDigitalDevices, TVisitor, TVisitorDigitalDevices } from './VisitRequest';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { DevicesOther } from '@mui/icons-material';
import { formatCompanyPhoneNumber, formatPhoneNumber, isValidCompanyPhoneNumber, isValidPhoneNumber } from 'constants/regularExpression';
import { useSnackbarStore } from 'store/useSnackbarStore';
import { useTranslation } from 'react-i18next';
import './VisitPopup.css';

interface VisitPopupProps {
  digitalDevices?: TDigitalDevices[]; //안쓸듯 쓸듯
  visitor?: TVisitor;
  addVisitor: (visitor: any) => void;
  updateVisitor: (visitor: any) => void;
  newId: () => number;
  open: boolean;
  onClose: () => void;
}

const VisitPopup: React.FC<VisitPopupProps> = ({ digitalDevices, visitor, addVisitor, updateVisitor, newId, open, onClose }) => {
  const [visitorDevices, setVisitorDevices] = useState<TVisitorDigitalDevices[]>([]);
  const [visitorData, setVisitorData] = useState<TVisitor>(initVisitorData);
  const [birthError, setBirthError] = useState<string>(''); // 생년월일 에러 메시지 상태
  const [companyPhoneError, setCompanyPhoneError] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>(''); // 전화번호 에러 상태
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
  // 다크 라이트 모드
  const theme = useTheme();
  // 다국어
  const { t } = useTranslation();

  // visitor prop이 변경될 때마다 상태 초기화
  useEffect(() => {
    if (visitor) {
      setVisitorData(visitor);
      setVisitorDevices(visitor.digitalDevices || []);
    } else {
      // 추가하는 경우 빈 초기값으로 설정
      setVisitorData(initVisitorData);
      setVisitorDevices([]);
    }
  }, [visitor]);

  // 입력 값이 변경될 때 상태 업데이트
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'phoneNumber') {
      const formattedValue = formatPhoneNumber(value);
      setPhoneError(''); // 입력중에 에러 초기화
      setVisitorData((prevData) => ({
        ...prevData,
        [name]: formattedValue
      }));
    } else if (name === 'companyPhoneNumber') {
      const formattedValue = formatCompanyPhoneNumber(value);
      setCompanyPhoneError(''); // 입력중에 에러 초기화
      setVisitorData((prevData) => ({
        ...prevData,
        [name]: formattedValue
      }));
    } else if (name === 'email') {
      setEmailError(''); // 입력중에 에러 초기화
      setVisitorData((prevData) => ({
        ...prevData,
        [name]: value
      }));
    } else {
      setVisitorData((prevData) => ({
        ...prevData,
        [name]: value
      }));
    }
  };

  // 디지털 기기 입력 값 변경
  const handleDeviceChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedDevices = visitorDevices.map((device, i) => (i === index ? { ...device, [name]: value } : device));
    // console.log('디지털 기기 입력 값 변경 : ', updatedDevices);
    setVisitorDevices(updatedDevices);
  };

  const handleDigitalDeviceTypeChange = (index: number, event: SelectChangeEvent) => {
    const digitalDeviceId = Number(event.target.value as unknown);
    const updatedDevices = visitorDevices.map((device, i) => (i === index ? { ...device, digitalDeviceId: digitalDeviceId } : device));
    setVisitorDevices(updatedDevices);
  };

  // 디지털 기기 추가
  const handleAddDevice = () => {
    const newDevice = {
      digitalDeviceId: 1,
      digitalDeviceName: '',
      modelName: '',
      serialNumber: '',
      note: ''
    };
    setVisitorDevices((prevDevices) => [...prevDevices, newDevice]);
  };

  // 디지털 기기 삭제
  const handleDeleteDevice = (index: number) => {
    setVisitorDevices((prevDevices) => prevDevices.filter((_, i) => i !== index));
  };

  // 디지털 기기 없음
  const handleNoCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setVisitorDevices([]); // 체크되면 디지털 기기 데이터를 빈 배열로 설정
    }
  };

  // 생년월일
  const handleDateChange = (newDate: Dayjs | null) => {
    if (newDate) {
      const today = dayjs();
      const minAgeDate = newDate.set('year', newDate.year() + 19); // 생일이 지나서 만 19세가 된 날짜 계산
      const isUnderAge = today.isBefore(minAgeDate); // 오늘이 만 19세가 되기 전이라면

      if (isUnderAge) {
        setBirthError(t('visitor_info.message.birth_error')); // 만 19세 미만의 경우 에러메시지
        setVisitorData((prevData) => ({
          ...prevData,
          birth: '' // 잘못된 날짜가 선택되었으므로 birth 초기화
        }));
      } else {
        setBirthError(''); // 만 19세 이상일 경우 에러 메시지 초기화
        setVisitorData((prevData) => ({
          ...prevData,
          birth: newDate ? newDate.format('YYYY-MM-DD') : '' // null일 경우 빈 문자열로 설정
        }));
      }
    }
  };
  const requiredFields = [
    { key: 'name', label: t('visitor_info.name') },
    { key: 'birth', label: t('visitor_info.birth') },
    { key: 'phoneNumber', label: t('visitor_info.phone') },
    { key: 'email', label: t('visitor_info.email') },
    { key: 'companyName', label: t('visitor_info.company_name') },
    { key: 'companyPhoneNumber', label: t('visitor_info.company_phone') }
    // { key: 'bloodType', label: t('visitor_info.blood_type') }
  ];

  const validateRequiredFields = (fields: typeof requiredFields) => {
    for (const field of fields) {
      if (!visitorData[field.key]) {
        showSnackbar(`${t('visitor_info.message.field_error')} ${field.label}`, 'warning');
        return false;
      }
    }
    if (visitorDevices.length === 0) {
      return true;
    }
    for (const device of visitorDevices) {
      if (!device.digitalDeviceId) {
        showSnackbar(t('visitor_info.message.digital_type_error'), 'warning');
        return false;
      }
      if (!device.modelName) {
        showSnackbar(t('visitor_info.message.model_name_error'), 'warning');
        return false;
      }
    }
    return true;
  };

  // 저장 혹은 수정버튼 클릭했을때 일어나는 로직
  const handleSaveOrEdit = (isEdit: boolean) => {
    const { id, ...visitorDataExceptId } = visitorData;
    // 유효성 검사
    if (!validateRequiredFields(requiredFields)) {
      return;
    }
    const newOrEditVisitor = {
      id: isEdit ? visitorData.id : newId(),
      ...visitorDataExceptId,
      digitalDevices: visitorDevices // 기기 정보 포함
    };

    if (isEdit) {
      updateVisitor(newOrEditVisitor); // 수정
    } else {
      addVisitor(newOrEditVisitor); // 추가
    }

    setVisitorDevices([]); // 디지털기기 초기화
    setVisitorData(initVisitorData); // 방문자 초기화
    onClose(); // 팝업 닫기
  };

  // 닫기 버튼과 함께 모든 상태를 리셋하는 함수
  const handleCloseAndReset = () => {
    setVisitorDevices([]); //디지털기기 초기화
    setVisitorData(initVisitorData); //방문자 초기화
    onClose(); // 팝업 닫기
  };

  // 포커스가 벗어났을 때 유효성 검사 실행
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'phoneNumber') {
      // 전화번호 유효성 검사
      if (!value) {
        setPhoneError(t('visitor_info.validation.phone_required')); // 값이 비어있을 경우
      } else if (!isValidPhoneNumber(value)) {
        setPhoneError(t('visitor_info.validation.invalid_phone_number')); // 유효하지 않은 형식일 경우
      } else {
        setPhoneError(''); // 유효한 경우 에러 초기화
      }
    } else if (name === 'email') {
      // 이메일 유효성 검사
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // 이메일 유효성 검사 정규식

      if (!value) {
        setEmailError(t('visitor_info.validation.email_required')); // 값이 비어있을 경우
      } else if (!emailRegex.test(value)) {
        setEmailError(t('visitor_info.validation.invalid_email')); // 유효하지 않은 이메일 형식일 경우
      } else {
        setEmailError(''); // 유효한 경우 에러 초기화
      }
    } else if (name === 'companyPhoneNumber') {
      // 회사 연락처 유효성 검사
      if (!value) {
        setCompanyPhoneError(t('visitor_info.validation.company_phone_required')); // 값이 비어있을 경우
      } else if (!isValidCompanyPhoneNumber(value)) {
        setCompanyPhoneError(t('visitor_info.validation.invalid_company_phone')); // 유효하지 않은 형식일 경우
      } else {
        setCompanyPhoneError(''); // 유효한 경우 에러 초기화
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '400px',
          maxWidth: '400px',
          maxHeight: '600px',
          margin: 'auto'
        }
      }}
    >
      <DialogTitle className="headerTitle" sx={{ bgcolor: theme.palette.table.header, border: 'none' }}>
        {/* 방문자 추가/수정 */}
        <span>
          {t('visitor_info.visitor')} {visitorData.id === 0 ? t('visitor_info.add') : t('visitor_info.edit')}
        </span>

        {/* 엑스 버튼 오른쪽에 배치 */}
        <IconButton onClick={handleCloseAndReset} sx={{ marginLeft: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: theme.palette.mode == 'dark' ? '#4c4c4c' : '#ffffff', paddingTop: '20px !important', paddingBottom: '2px' }}>
        <Box display="flex" flexDirection="column" gap={1}>
          {/* 이름 */}
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('visitor_info.name')}
              <FormLabel required sx={{ fontSize: '0.75rem' }} />
            </Typography>
            <TextField
              required
              name="name"
              value={visitorData.name || ''}
              size="small"
              placeholder={t('visitor_info.message.name')}
              onChange={handleChange}
              sx={{ flexGrow: 1 }}
            />
          </Box>
          {/* 생년월일 */}
          <Box display="flex" flexDirection="row" alignItems="start" gap={1}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title} height={32} lineHeight={2.75}>
              {t('visitor_info.birth')}
              <FormLabel required sx={{ fontSize: '0.75rem' }} />
            </Typography>
            <DatePicker
              name="birth"
              format="YYYY-MM-DD"
              value={visitorData.birth ? dayjs(visitorData.birth) : null}
              onChange={(newValue) => handleDateChange(newValue)}
              slotProps={{
                calendarHeader: {
                  format: 'YYYY-MM'
                },
                textField: { placeholder: t('datepicker_placeholder') || 'YYYY-MM-DD' }
              }}
              sx={{
                flexGrow: 1,
                width: '140px',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.25rem' // 아이콘 크기 조정
                }
              }}
            />
          </Box>
          {birthError && (
            <FormHelperText error sx={{ marginLeft: 11 }}>
              {birthError}
            </FormHelperText>
          )}
          {/* 전화번호 */}
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('visitor_info.phone')}
              <FormLabel required sx={{ fontSize: '0.75rem' }} />
            </Typography>
            <TextField
              required
              name="phoneNumber"
              value={visitorData.phoneNumber || ''}
              placeholder="010-0000-000"
              size="small"
              onChange={handleChange}
              onBlur={handleBlur} // 포커스가 벗어날 때 검사
              error={Boolean(phoneError)} // 에러 상태
              helperText={phoneError} // 에러 메시지
              sx={{ flexGrow: 1 }}
              inputProps={{
                inputMode: 'numeric', // 모바일에서 숫자키패드
                pattern: '[0-9]*' // 숫자만 입력
              }}
            />
          </Box>
          {/* 이메일 */}
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('visitor_info.email')}
              <FormLabel required sx={{ fontSize: '0.75rem' }} />
            </Typography>
            <TextField
              required
              name="email"
              id="email"
              type="email"
              value={visitorData.email || ''}
              size="small"
              placeholder={t('visitor_info.message.email')}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(emailError)} // 에러 상태
              helperText={emailError} // 에러 메시지
              sx={{ flexGrow: 1 }}
            />
          </Box>
          {/* 회사이름 */}
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('visitor_info.company_name')}
              <FormLabel required sx={{ fontSize: '0.75rem' }} />
            </Typography>
            <TextField
              required
              name="companyName"
              value={visitorData.companyName || ''}
              size="small"
              placeholder={t('visitor_info.message.company_name')}
              onChange={handleChange}
              sx={{ flexGrow: 1 }}
            />
          </Box>
          {/* 회사연락처 */}
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('visitor_info.company_phone')}
              <FormLabel required sx={{ fontSize: '0.75rem' }} />
            </Typography>
            <TextField
              required
              name="companyPhoneNumber"
              value={visitorData.companyPhoneNumber || ''}
              placeholder="031-000-0000"
              size="small"
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(companyPhoneError)} // 에러 상태
              helperText={companyPhoneError} // 에러 메시지
              sx={{ flexGrow: 1 }}
            />
          </Box>
          {/* 혈액형 */}
          {/*<Box display="flex" flexDirection="row" alignItems="center" gap={1}>*/}
          {/*  <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>*/}
          {/*    {t('visitor_info.blood_type')}*/}
          {/*    <FormLabel required sx={{ fontSize: '0.75rem' }} />*/}
          {/*  </Typography>*/}
          {/*  <Select*/}
          {/*    name="bloodType"*/}
          {/*    value={visitorData.bloodType || ''} // 현재 선택된 값*/}
          {/*    variant="outlined"*/}
          {/*    size="small"*/}
          {/*    sx={{ flexGrow: 1, width: '100px' }}*/}
          {/*    onChange={(e: any) => handleChange(e)} // 값 변경 시 호출되는 핸들러*/}
          {/*  >*/}
          {/*    {['A', 'B', 'AB', 'O', t('visitor_info.none')].map((type: string) => (*/}
          {/*      <MenuItem key={type} value={type} sx={{ fontSize: '12px' }}>*/}
          {/*        {type}*/}
          {/*      </MenuItem>*/}
          {/*    ))}*/}
          {/*  </Select>*/}
          {/*</Box>*/}

          <Alert
            severity="info"
            sx={{
              bgcolor: theme.palette.mode == 'dark' ? '#5872A5' : 'primary',
              paddingY: 0,
              fontSize: '0.75rem',
              '& .MuiAlert-message': { paddingTop: '12px' },
              '& .MuiAlert-icon': { color: theme.palette.mode == 'dark' ? '#B7DDFB' : 'primary' }
            }}
          >
            {t('visitor_info.message.car')}
          </Alert>
          {/* 방문 차량번호 */}
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('visitor_info.car')}
            </Typography>
            <TextField name="carNumber" value={visitorData.carNumber || ''} placeholder="000가0000" size="small" onChange={handleChange} sx={{ flexGrow: 1 }} />
          </Box>
          {/* 메모 */}
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('visitor_info.memo')}
            </Typography>
            <TextField
              name="note"
              value={visitorData.note || ''}
              size="small"
              maxRows={2}
              multiline
              onChange={handleChange}
              sx={{
                flexGrow: 1,
                '& .MuiInputBase-root': {
                  paddingX: 0
                },
                '& .MuiOutlinedInput-root': {
                  height: 'auto'
                }
              }}
            />
          </Box>

          {/* 디지털 기기 추가 */}
          <Box borderRadius={1} bgcolor={theme.palette.mode == 'dark' ? '#303030' : '#f5f5f5'} padding={2} display="flex" flexDirection="column" gap={1}>
            {/* 디지털 기기 제목 및 아이콘, 체크박스와 없음 버튼 */}
            <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
              {/* 디지털 기기와 아이콘 버튼 묶음 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Typography fontSize="1rem" fontWeight="bold">
                  {t('visitor_info.digital_devices.title')}
                </Typography>
                <IconButton color="primary" onClick={handleAddDevice} sx={{ padding: '2px', position: 'relative' }}>
                  <DevicesOther sx={{ fontSize: '20px' }} />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -2,
                      right: -10
                    }}
                  >
                    <AddIcon sx={{ fontSize: '14px' }} />
                  </Box>
                </IconButton>
              </Box>

              {/* 체크박스 없음 버튼 */}
              <FormControlLabel
                control={<Checkbox size="small" checked={visitorDevices?.length == 0} onChange={handleNoCheckboxChange} sx={{ padding: '2px' }} />}
                label={t('visitor_info.digital_devices.none')}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem'
                  }
                }}
              />
            </Box>

            {visitorDevices.map((device, index) => (
              <Box
                key={index}
                borderRadius={1}
                bgcolor={theme.palette.mode == 'dark' ? '#4c4c4c' : '#ffffff'}
                boxShadow={1}
                padding={2}
                display="flex"
                flexDirection="column"
                gap={1}
              >
                {/* 종류 */}
                <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                  <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                    {t('visitor_info.digital_devices.type')}
                    <FormLabel required sx={{ fontSize: '0.75rem' }} />
                  </Typography>
                  <Select
                    required
                    value={device.digitalDeviceId || ''} // 현재 선택된 값
                    variant="outlined"
                    size="small"
                    sx={{ flexGrow: 1, width: '100px' }}
                    onChange={(e: any) => handleDigitalDeviceTypeChange(index, e)} // 값 변경 시 호출되는 핸들러
                  >
                    {digitalDevices?.map((device) => (
                      <MenuItem key={device.id} value={device.id} sx={{ fontSize: '12px' }}>
                        {device.name}
                      </MenuItem>
                    ))}
                  </Select>

                  <IconButton color="error" onClick={() => handleDeleteDevice(index)}>
                    <DeleteIcon sx={{ fontSize: '20px' }} />
                  </IconButton>
                </Box>
                {/* 모델명 */}
                <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                  <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                    {t('visitor_info.digital_devices.model_name')}
                    <FormLabel required sx={{ fontSize: '0.75rem' }} />
                  </Typography>
                  <TextField
                    required
                    name="modelName"
                    value={device?.modelName || ''}
                    size="small"
                    onChange={(e: any) => handleDeviceChange(index, e)}
                    sx={{ flexGrow: 1 }}
                  />
                </Box>
                {/* 일련번호 */}
                <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                  <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                    {t('visitor_info.digital_devices.serial_number')}
                  </Typography>
                  <TextField
                    name="serialNumber"
                    value={device?.serialNumber || ''}
                    size="small"
                    onChange={(e: any) => handleDeviceChange(index, e)}
                    sx={{ flexGrow: 1 }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: '20px 24px', bgcolor: theme.palette.mode == 'dark' ? '#4c4c4c' : '#ffffff' }}>
        {visitorData.id === 0 ? (
          // 추가
          <Button
            variant="contained"
            color="primary"
            size="medium"
            fullWidth
            onClick={() => handleSaveOrEdit(false)}
            sx={{ borderRadius: '4px', fontWeight: 'bold' }}
          >
            {t('visitor_info.btn.add')}
          </Button>
        ) : (
          // 수정
          <Button
            variant="contained"
            color="primary"
            size="medium"
            fullWidth
            onClick={() => handleSaveOrEdit(true)}
            sx={{ borderRadius: '4px', fontWeight: 'bold' }}
          >
            {t('visitor_info.btn.edit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VisitPopup;
